import React, { createContext, useContext, useState, useEffect } from 'react';

// Importando as traduções
import pt from './locales/pt.json';
import en from './locales/en.json';
import es from './locales/es.json';

// Dicionário de traduções
const translations: Record<string, any> = {
  pt,
  en,
  es,
  // Outros idiomas podem ser adicionados aqui
};

interface I18nContextType {
  language: string;
  t: (key: string) => string;
  changeLanguage: (lang: string) => void;
  getTranslation: (key: string) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{
  children: React.ReactNode;
  initialLanguage?: string;
}> = ({ children, initialLanguage = 'pt' }) => {
  const [language, setLanguage] = useState<string>(initialLanguage);
  
  // Este useEffect garante que o idioma será atualizado
  // sempre que initialLanguage mudar, mesmo após a montagem inicial
  useEffect(() => {
    setLanguage(initialLanguage);
  }, [initialLanguage]);

  // Função para navegar em um objeto usando uma string de caminho (ex: "checkout.name")
  const getNestedValue = (obj: any, path: string[]): any => {
    if (!obj || path.length === 0) return undefined;
    if (path.length === 1) return obj[path[0]];
    return getNestedValue(obj[path[0]], path.slice(1));
  };

  // Função para obter uma tradução baseada em uma string de caminho (ex: "checkout.name")
  const getTranslation = (path: string): string => {
    if (!path) return '';
    
    const keys = path.split('.');
    
    // Primeiro, tente usar o idioma atual
    const currentLangTranslations = translations[language];
    let result;
    
    // Se o idioma selecionado estiver disponível nas traduções
    if (currentLangTranslations) {
      result = getNestedValue(currentLangTranslations, keys);
      if (typeof result === 'string') {
        return result;
      }
    }

    // Se não encontrado ou idioma não existir, use português como fallback
    const fallbackResult = getNestedValue(translations.pt, keys);
    if (typeof fallbackResult === 'string') {
      return fallbackResult;
    }

    // Se não encontrar nada, retorne a própria chave
    return path;
  };

  // Alias para getTranslation
  const t = getTranslation;

  const changeLanguage = (lang: string) => {
    setLanguage(lang);
  };

  return (
    <I18nContext.Provider value={{ language, t, changeLanguage, getTranslation }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
